import { PDFDict, PDFDocument, PDFName } from 'pdf-lib';

export type MakeFileAndUrlResult = { blob: Blob; url: string; revokeUrl: () => void };

/**
 * Creates a Blob and URL from a string. The URL should be revoked when no longer needed (e.g. `URL.revokeObjectURL(url)`). The `revokeUrl` function is a convenience for revoking the URL.
 */
export const makeFileAndUrl = (data: string | Buffer, mimeType: string, charset = 'utf-8'): MakeFileAndUrlResult => {
  // BlobPart takes an ArrayBufferView<ArrayBuffer>, and a Node Buffer is typed over ArrayBufferLike.
  const blobPart = typeof data === 'string' ? data : new Uint8Array(data);
  const blob = new Blob([blobPart], { type: `${mimeType};charset=${charset};` });
  const url = URL.createObjectURL(blob);

  return {
    blob,
    url,
    revokeUrl: () => URL.revokeObjectURL(url),
  };
};

// Helper: Check if a PDFDict has JavaScript entries
const JS_KEYS = ['JavaScript', 'JS', 'OpenAction', 'AA', 'Names'];
const dictHasJavaScript = (dict: PDFDict): boolean => {
  for (const key of JS_KEYS) {
    const val = dict.get(PDFName.of(key));
    if (val) return true;
  }
  return false;
};

const scanPdfForJavaScript = async (pdf: string | Uint8Array | ArrayBuffer): Promise<boolean> => {
  const pdfDoc = await PDFDocument.load(pdf);

  const catalog = pdfDoc.catalog;

  // 1. Check catalog dictionary
  if (dictHasJavaScript(catalog)) return true;

  // 2. Check pages for JavaScript actions
  const pages = pdfDoc.getPages();
  for (const page of pages) {
    const dict = page.node;
    if (dictHasJavaScript(dict)) return true;
  }

  // 3. Check document-level JavaScript in the Names dictionary
  const names = catalog.get(PDFName.of('Names'));
  if (names instanceof PDFDict) {
    const js = names.get(PDFName.of('JavaScript'));
    if (js) return true;
  }

  return false;
};

export type FileValidationStatus = {
  status: 'pending' | 'success' | 'error';
  errorMessage?: string;
};

const validatePdf = async (file: File): Promise<FileValidationStatus> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const hasJs = await scanPdfForJavaScript(arrayBuffer);
    if (hasJs) {
      return {
        status: 'error',
        errorMessage:
          'File contains potentially harmful JavaScript code.\n\nTry creating a clean PDF by:\n\nRight-click the PDF → Open with → Google Chrome\nPress Ctrl + P.\n\nDestination → Save as PDF.\n\nThen upload the cleaned file.',
      };
    }
    return { status: 'success' };
  } catch {
    return { status: 'error', errorMessage: 'Error processing file' };
  }
};

export const validateFile = async (file: File): Promise<FileValidationStatus> => {
  if (file.type === 'application/pdf') {
    return await validatePdf(file);
  }
  return { status: 'success' };
};
